import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {selectAssetValuationsSelectionForCopyWorkflow, selectCopyConfig} from "../../../store/fund/fund.selectors";
import {Store} from "@ngrx/store";
import {map, Subscription} from "rxjs";
import {AssetValuationCopyConfig, CopyConfig, FundValuationCopyConfig} from "../../../models/copy-config";
import {CopyFundReportAndValuationsActions} from "../../../store/fund.actions";
import {FormControl, FormGroup, NonNullableFormBuilder} from "@angular/forms";
import {AssetValuationsSelectionRow, AssetValuationType} from "../../../models/asset-valuation-selection-row";
import {AssetValuationTypeEnum} from "../../../../asset/models/asset-valuation/asset-valuation-type.enum";
import {CodeTableEnum} from "../../../../shared/model/code";

export type CopyAssetValuationsFormGroup = FormGroup<{
    assetId: FormControl<number>;
    dmvId: FormControl<number>;
    fovId?: FormControl<number>;
    copyDmv?: FormControl<boolean>;
    copyFov?: FormControl<boolean>;
    preferred: FormControl<AssetValuationType>
}>

@Component({
    selector: "valumize-asset-valuation-copy-dialog",
    templateUrl: "./asset-valuation-copy-dialog.component.html",
    styleUrl: "./asset-valuation-copy-dialog.component.scss",
    standalone: false
})
export class AssetValuationCopyDialogComponent implements OnInit, OnDestroy {
    @Input() fundId?: number;
    @Input() fundReportId?: number;

    @Output() previous = new EventEmitter<void>();
    @Output() completed = new EventEmitter<void>();

    private readonly store = inject(Store);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    subscriptions: Subscription[] = [];
    copyConfig?: CopyConfig;

    valuationTypes = Object.values(AssetValuationTypeEnum);
    codeTableValuationType = CodeTableEnum.ASSET_VALUATIONTYPE;

    assetValuationsSelection$ = this.store.select(selectAssetValuationsSelectionForCopyWorkflow);
    assetValuationsColumns = ["companyName", "dmv", "fov", "preferredValuation"]

    assetValuationsCopyForm = this.formBuilder.group({
        valuations: this.formBuilder.array<CopyAssetValuationsFormGroup>([]),
    });

    get valuationsFormArray() {
        return this.assetValuationsCopyForm.controls.valuations;
    }

    constructor() {
    }

    ngOnInit() {
        this.subscriptions.push(
            this.store.select(selectCopyConfig).pipe(
                map(copyConfig => this.copyConfig = copyConfig)
            ).subscribe());

        this.subscriptions.push(
            this.assetValuationsSelection$.pipe(
                map(valuationSelectionRows => {
                    this.valuationsFormArray.clear();
                    if (!valuationSelectionRows || valuationSelectionRows.length === 0) {
                        return;
                    }
                    valuationSelectionRows.forEach(row => {
                        const dmvCopyConfig = this.copyConfig?.fundValuationCopyConfig?.assetValuationsCopyConfig
                            .find(config => config.assetValuationId === row.dmv.assetValuationId);
                        const fovCopyConfig = this.copyConfig?.fundValuationCopyConfig?.assetValuationsCopyConfig
                            .find(config => config.assetValuationId === row.fov?.assetValuationId);

                        let preferred: AssetValuationType = row.preferredValuation;
                        if (dmvCopyConfig?.preferredValuation) {
                            preferred = AssetValuationTypeEnum.DiscountMultipleValuation;
                        } else if (fovCopyConfig?.preferredValuation) {
                            preferred = AssetValuationTypeEnum.FinancialOperationalValuation;
                        }

                        const group: CopyAssetValuationsFormGroup = this.formBuilder.group({
                            assetId: this.formBuilder.control(row.assetId),
                            dmvId: this.formBuilder.control(row.dmv.assetValuationId),
                            preferred: this.formBuilder.control(preferred)
                        });

                        if (row.dmv.isCopyAllowed) {
                            const checked = !!dmvCopyConfig;
                            group.addControl("copyDmv", this.formBuilder.control(checked))
                        }
                        if (row.fov && row.fov?.isCopyAllowed) {
                            const checked = !!fovCopyConfig;
                            group.addControl("fovId", this.formBuilder.control(row.fov.assetValuationId))
                            group.addControl("copyFov", this.formBuilder.control(checked))
                        }
                        this.valuationsFormArray.push(group);
                    })
                })).subscribe());
    }

    loadAssetValuations(): void {
        if (!this.fundId || !this.fundReportId || !this.copyConfig?.fundValuationCopyConfig?.fundValuationId) {
            return;
        }
        this.store.dispatch(CopyFundReportAndValuationsActions.loadAssetValuationsSelection({
            fundId: this.fundId,
            fundReportId: this.fundReportId,
            fundValuationId: this.copyConfig.fundValuationCopyConfig.fundValuationId,
            copyConfig: this.copyConfig
        }));
    }

    allDmvSelected = (): boolean => this.areAllSelected("copyDmv");

    someDmvSelected = (): boolean => this.areSomeSelected("copyDmv");

    toggleAllDmv = (checked: boolean): void => this.toggleAll("copyDmv", checked);

    allFovSelected = (): boolean => this.areAllSelected("copyFov");

    someFovSelected = (): boolean => this.areSomeSelected("copyFov");

    toggleAllFov = (checked: boolean): void => this.toggleAll("copyFov", checked);

    private getCopyControls(controlName: "copyDmv" | "copyFov"): FormControl<boolean>[] {
        return this.valuationsFormArray.controls
            .map(group => group.controls[controlName])
            .filter((control): control is FormControl<boolean> => !!control);
    }

    private areAllSelected(controlName: "copyDmv" | "copyFov"): boolean {
        const controls = this.getCopyControls(controlName);
        return controls.length > 0 && controls.every(control => control.value);
    }

    private areSomeSelected(controlName: "copyDmv" | "copyFov"): boolean {
        const controls = this.getCopyControls(controlName);
        const selectedCount = controls.filter(control => control.value).length;
        return selectedCount > 0 && selectedCount < controls.length;
    }

    private toggleAll(controlName: "copyDmv" | "copyFov", checked: boolean): void {
        this.getCopyControls(controlName).forEach(control => control.setValue(checked));
    }

    filterAvailableValuationTypes(valuationTypes: string[], valuationSelectionRow: AssetValuationsSelectionRow, index: number): string[] {
        if (!valuationSelectionRow.fov || !valuationSelectionRow.fov.isCopyAllowed || !this.valuationsFormArray.at(index).value.copyFov) {
            return valuationTypes.filter(vt => vt !== AssetValuationTypeEnum.FinancialOperationalValuation);
        }
        return valuationTypes;
    }

    navigateToSummaryPage() {
        this.updateCopyConfig();
        this.completed.emit()
    }

    navigateToFundValuationCopyDialog() {
        this.updateCopyConfig();
        this.previous.emit();
    }

    private updateCopyConfig() {
        const assetValuationCopyConfigs: AssetValuationCopyConfig[] = [];

        this.valuationsFormArray.controls.forEach((group: CopyAssetValuationsFormGroup) => {
            const formControls = group.controls;

            const copyDmv = !!formControls.copyDmv?.value;
            const copyFov = !!(formControls.fovId?.value && formControls.copyFov?.value);

            // Guarantee the preferred calculation base is a valuation type that is actually being copied.
            // The preferred dropdown can still hold a stale type (e.g. FOV) after its copy box is unchecked,
            // which would otherwise produce a company with a copied valuation but no preferred one (B-1205).
            let preferred = formControls.preferred.value;
            const preferredIsCopied =
                (preferred === AssetValuationTypeEnum.DiscountMultipleValuation && copyDmv)
                || (preferred === AssetValuationTypeEnum.FinancialOperationalValuation && copyFov);
            if (!preferredIsCopied) {
                preferred = copyDmv ? AssetValuationTypeEnum.DiscountMultipleValuation : AssetValuationTypeEnum.FinancialOperationalValuation;
            }

            if (copyDmv) {
                assetValuationCopyConfigs.push({
                    assetId: formControls.assetId.value,
                    assetValuationId: formControls.dmvId.value,
                    preferredValuation: preferred === AssetValuationTypeEnum.DiscountMultipleValuation
                });
            }

            if (copyFov && formControls.fovId?.value) {
                assetValuationCopyConfigs.push({
                    assetId: formControls.assetId.value,
                    assetValuationId: formControls.fovId.value,
                    preferredValuation: preferred === AssetValuationTypeEnum.FinancialOperationalValuation
                });
            }
        })

        if (!this.copyConfig?.fundValuationCopyConfig) {
            return;
        }
        const updatedFundValuationCopyConfig: FundValuationCopyConfig = {...this.copyConfig.fundValuationCopyConfig, assetValuationsCopyConfig: assetValuationCopyConfigs};
        this.copyConfig = {...this.copyConfig, fundValuationCopyConfig: updatedFundValuationCopyConfig};

        this.store.dispatch(CopyFundReportAndValuationsActions.updateCopyConfig({copyConfig: this.copyConfig}));
    }

    ngOnDestroy = () => this.subscriptions.forEach(sub => sub.unsubscribe());
}
